import { useEffect, useRef } from 'react';
import katex from 'katex';

interface MathRendererProps {
  content: string;
  className?: string;
}

export default function MathRenderer({ content, className = '' }: MathRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || !content) return;

    try {
      // Detect if content contains LaTeX patterns
      const hasLatex = /\\[a-zA-Z]+|[\^_{}]|\$\$?/.test(content);

      if (!hasLatex) {
        // No LaTeX detected, render as plain text
        containerRef.current.innerHTML = `<pre class="font-mono text-sm whitespace-pre-wrap">${escapeHtml(content)}</pre>`;
        return;
      }

      // Process content with LaTeX expressions
      const processedContent = processLatexContent(content);
      containerRef.current.innerHTML = processedContent;
    } catch (error) {
      // Fallback to plain text on error
      console.warn('KaTeX rendering failed:', error);
      if (containerRef.current) {
        containerRef.current.innerHTML = `<pre class="font-mono text-sm whitespace-pre-wrap">${escapeHtml(content)}</pre>`;
      }
    }
  }, [content]);

  return <div ref={containerRef} className={className} />;
}

function processLatexContent(text: string): string {
  let processed = text;

  // Handle display math ($$...$$)
  processed = processed.replace(/\$\$([\s\S]+?)\$\$/g, (match, latex) => {
    try {
      return katex.renderToString(latex.trim(), {
        displayMode: true,
        throwOnError: false,
        output: 'html',
      });
    } catch {
      return match;
    }
  });

  // Handle inline math ($...$)
  processed = processed.replace(/\$([^\$\n]+?)\$/g, (match, latex) => {
    try {
      return katex.renderToString(latex.trim(), {
        displayMode: false,
        throwOnError: false,
        output: 'html',
      });
    } catch {
      return match;
    }
  });

  // Detect common LaTeX patterns without $ delimiters and wrap them
  processed = processed.replace(
    /\\(?:frac|int|sum|prod|sqrt|lim|infty|partial|nabla|alpha|beta|gamma|delta|epsilon|theta|lambda|mu|pi|sigma|omega|cdot|times|div|pm|leq|geq|neq|approx|equiv|subset|supset|in|notin|forall|exists|rightarrow|leftarrow|Rightarrow|Leftarrow|mathbb|mathcal|text)\{[^}]*\}|\\(?:frac|sqrt)\{[^}]*\}\{[^}]*\}|[a-zA-Z0-9]+[\^_]\{[^}]+\}|[a-zA-Z0-9]+[\^_][a-zA-Z0-9]/g,
    (match) => {
      try {
        return katex.renderToString(match, {
          displayMode: false,
          throwOnError: false,
          output: 'html',
        });
      } catch {
        return match;
      }
    }
  );

  // Wrap in a styled container
  return `<div class="math-content font-mono text-sm whitespace-pre-wrap leading-relaxed">${processed}</div>`;
}

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}